import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { paymentsAPI, getApiError } from '../../services/api';
import { CreditCard, Loader2, CheckCircle, Shield, Lock } from 'lucide-react';

const StripeCheckout = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const order = location.state?.order;
  const [intentData, setIntentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!orderId) {
      navigate('/store/cart');
      return;
    }
    paymentsAPI.createStripeIntent({ order_id: parseInt(orderId) })
      .then((res) => {
        setIntentData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(getApiError(err, 'Error al preparar el pago'));
        setLoading(false);
      });
  }, [orderId, navigate]);

  const handleStripePayment = async () => {
    setPaying(true);
    setError('');

    try {
      await paymentsAPI.confirmStripePayment({
        order_id: parseInt(orderId),
        payment_intent_id: intentData?.payment_intent_id || `pi_test_${orderId}`,
        fee: intentData ? intentData.amount - (order?.total_amount || 0) : 0,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate(`/store/orders/${orderId}/confirmation`);
      }, 1500);
    } catch (err) {
      setError(getApiError(err, 'Error al procesar el pago'));
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="w-14 h-14 border-4 border-orange-200 border-t-orange-600 rounded-full"
        />
      </div>
    );
  }

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md mx-auto px-4 py-20 text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
        >
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
        </motion.div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Pago Exitoso</h2>
        <p className="text-gray-500">Redirigiendo a la confirmación...</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto px-4 py-12"
    >
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
        <div className="text-center mb-8">
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{ repeat: Infinity, duration: 3 }}
          >
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-900/30 dark:to-orange-800/30 rounded-2xl flex items-center justify-center">
              <CreditCard className="w-10 h-10 text-orange-600" />
            </div>
          </motion.div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pago con Tarjeta</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">
            Orden: {order?.order_number || `#${orderId}`}
          </p>
          <motion.p
            key={intentData?.amount || order?.total_amount}
            initial={{ scale: 1.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-4xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent mt-3"
          >
            ${intentData?.amount?.toFixed(2) || order?.total_amount?.toFixed(2) || '0.00'}
          </motion.p>
        </div>

        <div className="flex items-center justify-center gap-1 mb-6 text-xs text-gray-400">
          <Lock className="w-3 h-3" /> Pago seguro con Stripe
        </div>

        {!intentData?.stripe_publishable_key || intentData?.stripe_publishable_key === 'pk_test_placeholder' ? (
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border border-yellow-200 dark:border-yellow-700 rounded-2xl p-5"
            >
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-5 h-5 text-yellow-600" />
                <p className="font-semibold text-yellow-800 dark:text-yellow-200">Modo de Prueba</p>
              </div>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Stripe no está configurado con API keys reales. Usa el botón para simular el pago.
              </p>
            </motion.div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleStripePayment}
              disabled={paying}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3.5 rounded-xl font-semibold hover:shadow-lg hover:shadow-orange-500/30 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {paying ? <><Loader2 className="w-5 h-5 animate-spin" /> Procesando...</> : 'Simular Pago Exitoso'}
            </motion.button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
              <p className="text-sm text-gray-500 mb-2">Número de Tarjeta</p>
              <motion.div
                whileFocus={{ scale: 1.01 }}
                className="h-12 bg-white dark:bg-gray-600 rounded-xl border dark:border-gray-500 flex items-center px-4 text-gray-400 font-mono"
              >
                •••• •••• •••• ••••
              </motion.div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                <p className="text-sm text-gray-500 mb-2">Vencimiento</p>
                <div className="h-12 bg-white dark:bg-gray-600 rounded-xl border dark:border-gray-500 flex items-center px-4 text-gray-400 font-mono">
                  MM/AA
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                <p className="text-sm text-gray-500 mb-2">CVC</p>
                <div className="h-12 bg-white dark:bg-gray-600 rounded-xl border dark:border-gray-500 flex items-center px-4 text-gray-400 font-mono">
                  •••
                </div>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleStripePayment}
              disabled={paying}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3.5 rounded-xl font-semibold hover:shadow-lg hover:shadow-orange-500/30 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {paying ? <><Loader2 className="w-5 h-5 animate-spin" /> Procesando...</> : 'Pagar'}
            </motion.button>
          </div>
        )}

        <AnimatePresence>
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-red-600 text-sm mt-4 bg-red-50 dark:bg-red-900/30 p-3 rounded-xl border border-red-200 dark:border-red-700"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default StripeCheckout;
